import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

import { Text, Spinner } from '@zillow/constellation';
import { MstSentry } from '@zillow/mst-sentry';
import styled, { css } from 'styled-components';
import { yieldCallback } from '@zillow/yield-callback';
import { AD_SLOTS } from '@zillow/ad-state-machine';
import { trackNewLaneSRPEvent } from '../util/tracking/tracking';
import { BOOST_CLICK } from '../util/tracking/constant';
import { sentryScope } from '../../../util/tracking';

import {
    NAV_AD_TYPE,
    RENTAL_SECOND_AD_VERS,
    DEFAULT_VERS,
    VELOCITY_CONTROL_VERS,
} from '../constants';

// import ErrorBoundary from '../../ErrorBoundary';

import {
    getAdLoadSpeedTrial,
    getIsSecondNavAdSlot13Enabled,
    getIsSecondNavAdSlot17Enabled,
} from '../util/getFromState';
import { getNumListColumns, getIsMobileLayout } from '../util/getFromState';
import { getAdAbTrialVers, getAdTargetDiv } from '../util/common';
import DetectIFrameClick from './DetectIFrameClick';
import SearchAd from './SearchAd';

const StyledLoadingText = styled(Text)`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    position: absolute;
    top: 0;
    width: 100%;
`;

const StyledAdWrapper = styled.div`
    ${({ isIABAdStyle, isTall }) =>
        isIABAdStyle &&
        isTall &&
        css`
            position: absolute;
            top: 15px;
        `};
`;

export class NavAdContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            wasMounted: false,
        };
        this.handleClick = yieldCallback(this.handleClick.bind(this));
    }

    componentDidMount() {
        this.setState({
            wasMounted: true,
        });
    }

    handleClick() {
        const { adSlot, isSecondSlot13Enabled } = this.props;
        let gaLabel = adSlot === AD_SLOTS.NAV ? 'Slot 3' : 'Slot 17';
        if (adSlot === AD_SLOTS.NAV2 && isSecondSlot13Enabled) {
            gaLabel = 'Slot 13';
        }

        const eventObject = {
            category: 'Search',
            action: 'Boost Click',
            label: gaLabel,
        };
        trackNewLaneSRPEvent(BOOST_CLICK, eventObject);
    }

    /**
     * We only want to update the Ad component if the response from the server indicates that we are in
     * need of a refresh
     */
    shouldComponentUpdate(nextProps) {
        return (
            nextProps.shouldUpdateNavAd ||
            !this.state.wasMounted ||
            this.props.adsConfig?.navAdSlot !== nextProps.adsConfig?.navAdSlot ||
            this.props.isTall !== nextProps.isTall ||
            this.props.hideLoadingSpinner !== nextProps.hideLoadingSpinner ||
            this.props.isIABAd !== nextProps.isIABAd ||
            this.props.numListColumns !== nextProps.numListColumns
        );
    }

    /**
     * After we successfully update the NavAd, we should reset the flag in the redux store indicating
     * that we initiated an ad refresh. We only want to trigger an update once.
     */

    /**
     * Modifies targets sent to GPT
        + Adds custom ab trial information sent as vers, see addCustomVersions method
        + Adds page_type to adjust to sibling listcard height, which only client is aware of
     * @method modifyTargets
     * @private
     */
    modifyTargets(targets, numListColumns, isTall, isRentalsSecondAd) {
        let modifiedTargets = { ...targets, page_type: isTall ? 'tall' : 'short' };

        if (isRentalsSecondAd) {
            const vers = RENTAL_SECOND_AD_VERS;
            modifiedTargets = { ...modifiedTargets, vers };
        }

        return modifiedTargets;
    }

    isIABSupported({ numListColumns, isTall, isMobile }) {
        const isColumnEligible = numListColumns > 0;
        const deviceSupported = isMobile && isTall;
        if (deviceSupported) {
            return true;
        }
        return isColumnEligible;
    }

    isRentalsAd({ navAdSlot }) {
        const adUnitParts = navAdSlot ? navAdSlot.split('/') : [];
        const adUnit3 = adUnitParts.length >= 3 ? adUnitParts[2] : undefined;
        return adUnit3 === 'rent_general';
    }

    getAd() {
        const {
            adsConfig,
            numListColumns,
            onSlotRenderEnded,
            isIABAd,
            isTall,
            isMobile,
            containerId,
            adLoadSpeedTrial,
            hasBeenVisible,
            isSecondSlot13Enabled,
            adSlot,
        } = this.props;

        let { navAdSlot } = adsConfig;
        if (adSlot === AD_SLOTS.RENTAL) {
            if (!this.isRentalsAd({ navAdSlot })) {
                return null;
            }
        }

        if (adSlot === AD_SLOTS.NAV2 || adSlot === AD_SLOTS.RENTAL) {
            navAdSlot = navAdSlot.replace('search_1', 'search_2');
        }

        const isIABAdAllowed = this.isIABSupported({ numListColumns, isTall, isMobile });

        const isRentalsAd = this.isRentalsAd({ navAdSlot });

        const isPrebidEnabled = isIABAdAllowed;

        const webSpeedVers = getAdAbTrialVers(
            'MST_SRP_WEB_SPEED',
            adLoadSpeedTrial,
            VELOCITY_CONTROL_VERS
        );
        const secondSlotVers = isSecondSlot13Enabled
            ? 'z_srp_web_2ndad-13and17_test_a'
            : 'z_srp_web_2ndad-13and17_test_b';
        const mstVers = `${DEFAULT_VERS},${webSpeedVers},${secondSlotVers}`;

        const targetDiv = getAdTargetDiv(adSlot);

        const modifiedTargets = this.modifyTargets(
            adsConfig.targets,
            numListColumns,
            isTall,
            adSlot === AD_SLOTS.RENTAL
        );

        /**
         * Adding unique container ids for each nav ad on the page for CLS identification.
         * nav-ad-container-rentals-prebid
         * nav-ad-container-rentals
         * nav-ad-container-two-rentals
         * nav-ad-container
         * nav-ad-container-prebid
         */
        const navAdId = `${containerId}${isRentalsAd ? '-rentals' : ''}${
            isPrebidEnabled ? '-prebid' : ''
        }`;

        return (
            <DetectIFrameClick
                key="navAd"
                className="nav-ad-gpt-container"
                role="button"
                isTall={isTall}
                numListColumns={numListColumns}
                isIABAd={isIABAd}
                tabIndex={0}
                onClick={this.handleClick}
                onKeyDown={this.handleClick}
                id={navAdId}
                data-testid={navAdId}
            >
                <StyledAdWrapper isTall={isTall} isIABAdStyle={isIABAdAllowed && isIABAd}>
                    <SearchAd
                        adType={NAV_AD_TYPE}
                        isTall={isTall}
                        onSlotRenderEndedSlot={onSlotRenderEnded}
                        slot={navAdSlot}
                        iabEnabled={isIABAdAllowed}
                        targets={modifiedTargets}
                        renderAd={this.state.wasMounted}
                        targetDiv={targetDiv}
                        variant="zsrp"
                        isPrebidTrialOn={isPrebidEnabled}
                        versOn={mstVers}
                        targetRef={this.navRef}
                        hasBeenVisible={hasBeenVisible}
                    />
                </StyledAdWrapper>
            </DetectIFrameClick>
        );
    }

    render() {
        const { adsConfig, onSlotRenderEnded, hideLoadingSpinner } = this.props;

        const isMissingNavAdConfig = _isEmpty(adsConfig) || _isEmpty(adsConfig.navAdSlot);

        if (isMissingNavAdConfig) {
            onSlotRenderEnded({
                isEmpty: true,
                didAdError: false,
            });
            return null;
        }

        return (
            <>
                <StyledLoadingText
                    as="p"
                    fontType="bodySmallHeading"
                    data-testid="nav-ad-loading-container"
                >
                    {!hideLoadingSpinner && <Spinner data-testid="nav-ad-spinner" />}
                    {!hideLoadingSpinner ? 'Loading...' : null}
                </StyledLoadingText>
                {this.getAd()}
            </>
        );
    }
}

NavAdContainer.propTypes = {
    adsConfig: PropTypes.object.isRequired,
    isTall: PropTypes.bool,
    numListColumns: PropTypes.number.isRequired,
    onSlotRenderEnded: PropTypes.func,
    shouldUpdateNavAd: PropTypes.bool,
    hideLoadingSpinner: PropTypes.bool,
    adLoadSpeedTrial: PropTypes.string,
    containerId: PropTypes.string,
    adSlot: PropTypes.string.isRequired,
};

NavAdContainer.defaultProps = {
    onSlotRenderEnded: () => ({
        isEmpty: true,
        isError: false,
    }),
    hideLoadingSpinner: false,
    showRentalsSecondAdPrimaryPosition: false,
    containerId: 'nav-ad-container',
};

const mapStateToProps = state => ({
    isMobile: getIsMobileLayout(state),
    adsConfig: state.searchList.adsConfig,
    numListColumns: getNumListColumns(state),
    shouldUpdateNavAd: state.uiState.shouldUpdateNavAd,
    adLoadSpeedTrial: getAdLoadSpeedTrial(state),
    isSecondSlot13Enabled: getIsSecondNavAdSlot13Enabled(state),
    isSecondSlot17Enabled: getIsSecondNavAdSlot17Enabled(state),
});

const ConnectedNavAdContainer = connect(mapStateToProps)(NavAdContainer);

// We have to provide this fallback component because the implementing ListResultsContainer component
// wraps <NavAdContainer> in an <li>. If we just returned a null component there would be a blank
// space where the nav ad would normally be.
// By calling this callback with didAdError we force ListResultsContainer to not render the <li> or
// the <NavAdContainer />.
const FallbackComponent = props => {
    props.onSlotRenderEnded({ isError: true });
    return null;
};
const NavAdErrorBoundary = props => (
    <MstSentry sentryScope={sentryScope} fallback={FallbackComponent}>
        <ConnectedNavAdContainer {...props} />
    </MstSentry>
);

export default NavAdErrorBoundary;
